<script setup lang="ts">
/**
 * GridIconCards
 *
 * Flexible grid of cards with icons, headings, and optional descriptions.
 * Used for: Quick Facts, Benefits, Capabilities, etc.
 *
 * Interaction Rules:
 * - WITH link: Lift + Shadow + Green Line ("Take me there")
 * - WITHOUT link: Shadow deepen + Icon scale ("Look at this")
 */

import type { GridIconCardsProps } from "~~/shared/types";

const props = withDefaults(defineProps<GridIconCardsProps>(), {
  columns: 3,
  sectionBg: "white",
  visible: true,
});

const { resolveLink } = useServiceRoutes();
const { handleLink, isIntentLink } = useIntentModal();

const NuxtLink = resolveComponent("NuxtLink");

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

// Check if a link is external (starts with http)
const isExternalLink = (link: { href?: string; targetKey?: string } | undefined): boolean => {
  if (!link?.href) return false;
  return link.href.startsWith("http://") || link.href.startsWith("https://");
};

// Handle card click - intercept intent links and open modal
const handleCardClick = (e: Event, link: { href?: string; targetKey?: string } | undefined) => {
  if (!link?.href) return;
  if (isIntentLink(link.href)) {
    e.preventDefault();
    handleLink(link.href);
  }
};

const gridColsClass = computed(() => {
  switch (props.columns) {
    case 2:
      return "sm:grid-cols-2";
    case 4:
      return "sm:grid-cols-2 lg:grid-cols-4";
    case 3:
    default:
      return "sm:grid-cols-2 lg:grid-cols-3";
  }
});
</script>

<template>
  <section v-if="visible !== false" :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header (only if title provided) -->
      <div v-if="title" class="mb-12 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
        <p v-if="subtitle" class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600">
          {{ subtitle }}
        </p>
      </div>

      <!-- Cards Grid -->
      <div :class="['grid gap-8', gridColsClass]">
        <component
          :is="item.link ? (isExternalLink(item.link) ? 'a' : NuxtLink) : 'div'"
          v-for="(item, index) in items"
          :key="index"
          :to="item.link && !isExternalLink(item.link) ? resolveLink(item.link) : undefined"
          :href="item.link && isExternalLink(item.link) ? item.link.href : undefined"
          :target="isExternalLink(item.link) ? '_blank' : undefined"
          :rel="isExternalLink(item.link) ? 'noopener noreferrer' : undefined"
          class="group relative flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm transition-all duration-300"
          :class="[
            item.link
              ? 'cursor-pointer hover:-translate-y-1 hover:shadow-lg'
              : 'hover:shadow-md',
          ]"
          @click="handleCardClick($event, item.link)"
        >
          <!-- Icon -->
          <div class="mb-6">
            <div
              class="inline-flex items-center justify-center rounded-xl bg-mw-accent-50 p-3 transition-all duration-300"
              :class="
                item.link
                  ? 'group-hover:bg-mw-accent-100 group-hover:scale-110'
                  : 'group-hover:bg-mw-accent-100'
              "
            >
              <Icon :name="item.icon" size="48" class="text-mw-accent-500" />
            </div>
          </div>

          <!-- Heading -->
          <h3 class="mb-3 text-xl font-bold text-slate-900">
            {{ item.heading }}
          </h3>

          <!-- Description -->
          <p v-if="item.description" class="whitespace-pre-line leading-relaxed text-neutral-600">
            {{ item.description }}
          </p>

          <!-- Link Arrow (only for linked cards) -->
          <div
            v-if="item.link"
            class="mt-auto flex items-center gap-1 pt-4 font-semibold text-primary-400"
          >
            <span>{{ item.link.text }}</span>
            <Icon
              name="lucide:arrow-right"
              size="16"
              class="transition-transform duration-300 group-hover:translate-x-1"
            />
          </div>

          <!-- Hover Accent Line (only for linked cards) -->
          <div
            v-if="item.link"
            class="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
          ></div>
        </component>
      </div>
    </div>
  </section>
</template>
